import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Trash2, Building2, X } from 'lucide-react'
import { mockCoachingTieUps, type CoachingTieUp } from '@/data/admin-mock-data'

export function AdminCoachingTieUpsView() {
    const [tieUps, setTieUps] = useState<CoachingTieUp[]>(mockCoachingTieUps)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({ name: '', location: '', image: '', bannerUrl: '', courses: '', feesRange: '', students: '', rating: '', isActive: true })

    const openCreate = () => {
        setEditingId(null)
        setForm({ name: '', location: '', image: '', bannerUrl: '', courses: '', feesRange: '', students: '', rating: '', isActive: true })
        setModalOpen(true)
    }

    const openEdit = (t: CoachingTieUp) => {
        setEditingId(t.id)
        setForm({
            name: t.name, location: t.location, image: t.image, bannerUrl: t.bannerUrl || '',
            courses: t.courses || '', feesRange: t.feesRange || '',
            students: t.students, rating: t.rating, isActive: t.isActive
        })
        setModalOpen(true)
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, image: url }))
        }
    }

    const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, bannerUrl: url }))
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setTieUps(prev => prev.map(x => x.id === editingId ? { ...x, ...form } : x))
        } else {
            setTieUps(prev => [...prev, { ...form, id: `t-${Date.now()}` }])
        }
        setModalOpen(false)
    }

    const handleDelete = (id: string) => {
        if (confirm('Remove this institute from the partner listing?')) setTieUps(prev => prev.filter(t => t.id !== id))
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Coaching Tie-ups</h2>
                    <p className="text-sm text-theme-secondary">Manage partner institutes shown on the Partner Institutes page (public website)</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Plus className="h-5 w-5" />
                    Add Institute
                </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tieUps.map((t) => (
                    <GlassmorphicCard key={t.id} className="overflow-hidden">
                        <div className="relative h-36 overflow-hidden">
                            <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                            <div className="absolute top-2 right-2 flex gap-1">
                                <button onClick={() => openEdit(t)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-theme-primary"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Building2 className="h-4 w-4 text-primary-blue shrink-0" />
                                <h3 className="font-semibold text-theme-primary">{t.name}</h3>
                            </div>
                            <p className="text-sm text-theme-secondary mb-2">{t.location}</p>
                            <p className="text-sm text-theme-muted">{t.students} students · Rating {t.rating}</p>
                            <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${t.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                                {t.isActive ? 'Visible' : 'Hidden'}
                            </span>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Institute' : 'Add Institute'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Name</label>
                                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Institute name" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">City / Location</label>
                                <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="e.g. Kota, Multiple" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Logo URL / Upload</label>
                                    <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-theme-secondary mb-2" />
                                    <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="https://..." />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Banner URL / Upload</label>
                                    <input type="file" accept="image/*" onChange={handleBannerUpload} className="w-full text-sm text-theme-secondary mb-2" />
                                    <input value={form.bannerUrl} onChange={e => setForm(f => ({ ...f, bannerUrl: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="https://..." />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Courses Offered</label>
                                    <input value={form.courses} onChange={e => setForm(f => ({ ...f, courses: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="NEET, JEE" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Fees Range</label>
                                    <input value={form.feesRange} onChange={e => setForm(f => ({ ...f, feesRange: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="₹1L - ₹2L" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Students</label>
                                    <input value={form.students} onChange={e => setForm(f => ({ ...f, students: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="5,000+" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Rating</label>
                                    <input value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="4.8" />
                                </div>
                            </div>
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Visible on public page</span>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Save</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
